import {AsyncPipe} from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, inject } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import {Router} from '@angular/router';
import {ECheckoutStatus} from '@app/checkout/enums/checkout-status.enum';
import {CheckoutStep} from '@app/checkout/enums/checkout-step.enum';
import {OrderStatusEnum} from '@app/checkout/enums/order-status.enum';
import {ICheckoutComponent} from '@app/checkout/models/checkout-component.model';
import {Order} from '@app/checkout/models/order-request.model';
import {resetCheckoutStatus, updateOrderFromOrderInfo} from '@app/checkout/state/checkout.actions';
import {selectCheckoutStatus, selectOrderForm} from '@app/checkout/state/checkout.selector';
import {ErrorHandlerUtils} from '@app/core/utils/error-handler.utils';
import {StringUtils} from '@app/core/utils/string.utils';
import {OnlyDigits_Regex} from '@app/shared/constants';
import {ICountry} from '@app/shared/models/country.model';
import {ICustomerProfileState} from '@app/shared/models/header.model';
import {StepperService} from '@app/shared/services/stepper.service';
import {selectCustomerInstitution, selectCustomerProfile} from '@app/shared/state/header/header.selector';
import {selectCountries} from '@app/signUp/state/sign-up.selector';
import {Store} from '@ngrx/store';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {ConfirmationService, MessageService, SelectItem} from 'primeng/api';
import {Bind} from 'primeng/bind';
import {InputText} from 'primeng/inputtext';
import {Select} from 'primeng/select';
import {Observable, Subject, map, takeUntil, tap} from 'rxjs';
import {IOrderInfo} from '../../models/order-info.model';

@Component({
  selector: 'app-order-info',
  templateUrl: './order-info.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [FormsModule, ReactiveFormsModule, Bind, Select, InputText, AsyncPipe, TranslatePipe],
})
export class OrderInfoComponent implements OnDestroy, ICheckoutComponent {
  private readonly formBuilder = inject(FormBuilder);
  private readonly stepperService = inject(StepperService);
  private readonly confirmationService = inject(ConfirmationService);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly store = inject(Store);
  private readonly messageService = inject(MessageService);

  orderInfoForm: FormGroup<IOrderInfo>;

  orderData: Order | undefined;
  private readonly unsubscribe$ = new Subject<void>();
  checkoutState: ECheckoutStatus | undefined;
  checkoutStatus = ECheckoutStatus;
  countryPhonePrefixes: SelectItem[] = [];
  dropdown: ICountry | null = null;
  countries$: Observable<ICountry[]>;
  selectedCountry: ICountry | undefined;
  customerInstitution$: Observable<string>;
  nextInProgress = false;
  previousInProgress = false;
  cancelInProgress = false;
  draftInProgress = false;
  globalToast = 'global-toast';

  constructor() {
    this.orderInfoForm = this.formBuilder.group(
      {
        deliverByRegularMail: [true],
        phone: ['', [Validators.pattern(OnlyDigits_Regex)]],
        countryCode: ['', [Validators.pattern(OnlyDigits_Regex)]],
      },
      {
        validators: this.requireBothOrNeitherValidator(),
      }
    );

    this.countries$ = this.store.select(selectCountries).pipe(
      takeUntil(this.unsubscribe$),
      map(countries => {
        const flattenCountries = [...(countries ?? [])].sort((a, b) =>
          a.twoLetterIsoCode.localeCompare(b.twoLetterIsoCode)
        );
        return (
          flattenCountries.map(country => ({
            ...country,
            phoneNumberPrefixWithPlus: `(${country.twoLetterIsoCode}) +${country.phoneNumberPrefix}`,
          })) ?? []
        );
      })
    );

    this.store
      .select(selectOrderForm)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((order: Order) => {
          this.orderData = order;
          if (this.orderData?.casePhoneNumber) {
            this.orderInfoForm.get('phone')?.patchValue(this.orderData?.casePhoneNumber);
          }
          if (this.orderData?.casePrefixPhoneNumber) {
            this.orderInfoForm.get('countryCode')?.patchValue(this.orderData?.casePrefixPhoneNumber);
          }
        })
      )
      .subscribe();

    this.store
      .select(selectCustomerProfile)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((customerProfile: ICustomerProfileState | null) => {
          const phone = customerProfile?.phoneNumber;
          const countryCode = StringUtils.replaceSpecialCharacterPlusAndSpace(customerProfile?.prefixPhoneNumber ?? '');

          const phoneHasValidValue = !StringUtils.isEmpty(phone) && OnlyDigits_Regex.test(phone ?? '');
          const countryCodeHasValidValue = !StringUtils.isEmpty(countryCode) && OnlyDigits_Regex.test(countryCode);
          const hasOrderPhone = !StringUtils.isEmpty(this.orderData?.casePhoneNumber);
          const hasOrderCountryCode = !StringUtils.isEmpty(this.orderData?.casePrefixPhoneNumber);

          if (phoneHasValidValue && countryCodeHasValidValue && !hasOrderPhone && !hasOrderCountryCode) {
            this.orderInfoForm.patchValue({
              phone,
              countryCode,
            });
          }
        })
      )
      .subscribe();

    this.store
      .select(selectCheckoutStatus)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((checkoutStatus: ECheckoutStatus) => {
          this.checkoutState = checkoutStatus;
          if (checkoutStatus === ECheckoutStatus.SuccessFromOrderInfo) {
            this.orderInfoForm.markAsPristine();
            this.stepperService.emitNext(CheckoutStep.BillingInfo);
          }
          if (
            checkoutStatus === ECheckoutStatus.SuccessFromOrderInfo ||
            checkoutStatus === ECheckoutStatus.ErrorFromOrderInfo
          ) {
            this.nextInProgress = false;
            this.draftInProgress = false;
            this.cdr.markForCheck();
          }
        })
      )
      .subscribe();

    this.customerInstitution$ = this.store.select(selectCustomerInstitution);
  }

  requireBothOrNeitherValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const phone = control.get('phone');
      const countryCode = control.get('countryCode');
      if (phone && countryCode) {
        const phoneValue = phone.value as string | null | undefined;
        const countryCodeValue = countryCode.value as string | null | undefined;

        const phoneHasValue = !StringUtils.isEmpty(phoneValue) && OnlyDigits_Regex.test(phoneValue ?? '');
        const countryCodeHasValue = !StringUtils.isEmpty(countryCodeValue);
        const bothHasValue = phoneHasValue && countryCodeHasValue;
        const bothHasNoValue = StringUtils.isEmpty(phoneValue) && StringUtils.isEmpty(countryCodeValue);

        if (bothHasValue || bothHasNoValue) {
          countryCode.clearValidators();
          countryCode.setErrors(null);
          phone.clearValidators();
          phone.setErrors(null);
          this.cdr.markForCheck();
          return null;
        } else {
          if (phoneHasValue) {
            countryCode.setValidators([Validators.required]);
            countryCode.markAsTouched();
          } else {
            phone.setValidators([Validators.required, Validators.pattern(OnlyDigits_Regex)]);
            phone.markAsTouched();
          }
          this.cdr.markForCheck();
          return {
            countryCodeRequired: phoneHasValue,
            phoneRequired: countryCodeHasValue,
            phonePattern: !StringUtils.isEmpty(phoneValue) && !OnlyDigits_Regex.test(phoneValue ?? ''),
          };
        }
      }
      return null;
    };
  }

  isRequired(fieldName: string) {
    return !!this.orderInfoForm.get(fieldName)?.hasValidator(Validators.required);
  }

  onSubmit() {
    this.store.dispatch(resetCheckoutStatus());
    this.saveOrder();
  }

  saveOrder(isDraft = false) {
    const orderRequest = {
      ...this.orderData,
      productId: this.orderData?.orderItems[0]?.productId ?? this.orderData?.productId,
      deliverResultsByMailYN: this.orderInfoForm.controls.deliverByRegularMail.value,
      currentStep: isDraft ? null : CheckoutStep.OrderInfo,
    } as Order;

    const phone = this.orderInfoForm.get('phone');
    const countryCode = this.orderInfoForm.get('countryCode');
    const phoneHasValue = !StringUtils.isEmpty(phone?.value) && OnlyDigits_Regex.test(phone?.value ?? '');
    const countryCodeHasValue = !StringUtils.isEmpty(countryCode?.value);
    if (phone && countryCode && ((phoneHasValue && countryCodeHasValue) || isDraft)) {
      orderRequest.casePhoneNumber = phoneHasValue ? phone.value : null;
      orderRequest.casePrefixPhoneNumber = countryCodeHasValue ? countryCode.value : null;
    }

    this.store.dispatch(
      updateOrderFromOrderInfo({
        orderRequest: orderRequest,
        isDraft: isDraft,
      })
    );
  }

  disableNext() {
    return this.isOrderFormInvalid();
  }
  isOrderFormInvalid() {
    return (
      this.orderInfoForm.hasError('countryCodeRequired') ||
      this.orderInfoForm.hasError('phoneRequired') ||
      this.orderInfoForm.hasError('phonePattern')
    );
  }
  next() {
    const lastCompletedStep = this.orderData?.lastCompletedStep ?? -1;
    if (this.orderInfoForm.dirty || lastCompletedStep < CheckoutStep.OrderInfo) {
      if (this.orderInfoForm.valid) {
        this.nextInProgress = true;
        if (this.orderData) {
          this.orderData = {
            ...this.orderData,
            lastCompletedStep: lastCompletedStep > CheckoutStep.OrderInfo ? lastCompletedStep : CheckoutStep.OrderInfo,
            currentStep: CheckoutStep.BillingInfo,
            orderStatusId: OrderStatusEnum.New.toString(),
          };
        }
        this.store.dispatch(resetCheckoutStatus());
        this.saveOrder();
      }
    } else {
      this.stepperService.emitNext(CheckoutStep.BillingInfo);
    }
  }

  previous() {
    this.stepperService.emitPrevious(CheckoutStep.OrderInfo);
  }

  cancelOrder() {
    // use for custom cancel logic
  }

  saveDraftOrder() {
    if (this.orderData && this.orderData.lastCompletedStep >= CheckoutStep.OrderInfo && this.isOrderFormInvalid()) {
      ErrorHandlerUtils.showErrorMessage(
        this.messageService,
        this.globalToast,
        this.translate.instant('Checkout.InvalidFormMessage')
      );
      return;
    }
    this.draftInProgress = true;
    if (this.orderData) {
      this.orderData = {
        ...this.orderData,
        orderStatusId: OrderStatusEnum.New.toString(),
      };
    }
    this.saveOrder(true);
  }

  nextLoading() {
    return this.nextInProgress;
  }

  previousLoading() {
    return this.previousInProgress;
  }

  cancelLoading() {
    return this.cancelInProgress;
  }

  draftOrSubmitLoading() {
    return this.draftInProgress;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
